import { createEmptyRaciMatrix, prepareRaciMatrixForPersist } from './raciMatrixModel';
import store from '../../redux/store';
import { GetRaciMatrix, PutRaciMatrix } from '../../redux/raciMatrixSlice';

const storageKey = projectId => `lens_raci_matrix_v1_${projectId}`;

export const loadRaciMatrixFromLocalStorage = projectId => {
    if (!projectId) return null;
    try {
        const raw = localStorage.getItem(storageKey(projectId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            version: 1,
            groups: Array.isArray(parsed.groups)
                ? parsed.groups.map(({ id, name, order }) => ({ id, name, order }))
                : [],
            columns: Array.isArray(parsed.columns) ? parsed.columns : [],
            activities: Array.isArray(parsed.activities) ? parsed.activities : [],
            assignments: parsed.assignments && typeof parsed.assignments === 'object' ? parsed.assignments : {},
        };
    } catch {
        return null;
    }
};

export const clearRaciMatrixLocalStorage = projectId => {
    if (!projectId) return;
    localStorage.removeItem(storageKey(projectId));
};

const isMatrixEmpty = matrix => {
    if (!matrix) return true;
    return (
        (!matrix.groups || matrix.groups.length === 0) &&
        (!matrix.columns || matrix.columns.length === 0) &&
        (!matrix.activities || matrix.activities.length === 0) &&
        (!matrix.assignments || Object.keys(matrix.assignments).length === 0)
    );
};

/** Load matrix from API; optionally uplift legacy localStorage once. */
export const fetchRaciMatrix = async projectId => {
    if (!projectId) {
        return { matrix: createEmptyRaciMatrix(), canEdit: false };
    }

    const result = await store.dispatch(GetRaciMatrix(projectId));
    if (GetRaciMatrix.rejected.match(result)) {
        throw result.payload || new Error('Failed to load RACI matrix');
    }

    const data = result.payload;
    let matrix = {
        version: data?.version ?? 1,
        groups: data?.groups ?? [],
        columns: data?.columns ?? [],
        activities: data?.activities ?? [],
        assignments: data?.assignments ?? {},
    };

    const localDraft = loadRaciMatrixFromLocalStorage(projectId);
    if (isMatrixEmpty(matrix) && localDraft && !isMatrixEmpty(localDraft) && data?.can_edit) {
        const saveResult = await store.dispatch(PutRaciMatrix({ projectId, matrix: localDraft }));
        if (PutRaciMatrix.fulfilled.match(saveResult)) {
            clearRaciMatrixLocalStorage(projectId);
            const saved = saveResult.payload;
            matrix = {
                version: saved?.version ?? 1,
                groups: saved?.groups ?? [],
                columns: saved?.columns ?? [],
                activities: saved?.activities ?? [],
                assignments: saved?.assignments ?? {},
            };
        }
    }

    return {
        matrix,
        canEdit: data?.can_edit !== false,
    };
};

export const persistRaciMatrix = async (projectId, matrix) => {
    if (!projectId) return { ok: false };
    const result = await store.dispatch(PutRaciMatrix({ projectId, matrix: prepareRaciMatrixForPersist(matrix) }));
    if (PutRaciMatrix.rejected.match(result)) {
        throw result.payload || new Error('Failed to save RACI matrix');
    }
    return { ok: true };
};
